import {MediaMatcher} from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Component, EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {StartupService} from "../../../service-ui/startup.service";
import {LoggedInCallback} from "../../../service-auth/cognito.service";
import {Subject} from "rxjs";
import { DraftService } from '../../../service-data/draft.service';
import { DraftBodyService } from '../../../service-data/draft-body.service';
import { DraftCoverService } from '../../../service-data/draft-cover.service';
import { Draft } from '../../../../model/Draft';
import { ResourceService } from '../../../service-data/resource.service';
import { Resource } from '../../../../model/Resource';
import {
  buildUploadForm,
  classifyUploadFailure,
  exceedsMaxBytes,
  formatBytes,
  isUploadSuccess,
  ResourceUploadTicket,
  UploadFailureKind
} from '../../../service-data/resource-upload';
import { isRateLimited, parseRateLimit } from '../../../service-data/http-error';
import { ResourceStatus } from '../../../service-ui/resource-status';
import { LanguagePipe } from '../../../pipe/language.pipe';
import { LANGUAGES, LanguageEntry } from '../../../../model/Languages';
import { ProjectDraftBodyComponent } from './project-draft-body/project-draft-body.component';
import { MatDialog } from '@angular/material/dialog';
import { ProjectService } from '../../../service-data/project.service';
import { modernDialogConfig } from '../../../service-ui/dialog-config';
import {
  evaluatePartitionPrompt,
  PartitionMember,
  PartitionProperty
} from '../../../util/project-partition';
import {
  DraftPartitionPromptComponent,
  DraftPartitionPromptData,
  DraftPartitionPromptResult
} from '../project/dialogs/draft-partition-prompt/draft-partition-prompt.component';

export interface TrimSizePreset {
  label: string;
  width: number;  // in inches
  height: number; // in inches
  isPromoted?: boolean; // For most common sizes
}

export const TRIM_SIZE_PRESETS: TrimSizePreset[] = [
  // Most common sizes (promoted)
  { label: '5.5 × 8.5', width: 5.5, height: 8.5, isPromoted: true },
  { label: '6 × 9', width: 6, height: 9, isPromoted: true },
  { label: '8.5 × 11', width: 8.5, height: 11, isPromoted: true },
  // Other sizes sorted by width, then height
  { label: '4 × 6', width: 4, height: 6 },
  { label: '5 × 7', width: 5, height: 7 },
  { label: '5 × 8', width: 5, height: 8 },
  { label: '5.25 × 8.25', width: 5.25, height: 8.25 },
  { label: '7 × 10', width: 7, height: 10 }
];

@Component({
  selector: 'app-project-draft',
  templateUrl: './project-draft.component.html',
  styleUrls: ['./project-draft.component.css']
})
export class ProjectDraftComponent implements OnDestroy, OnInit, OnChanges, LoggedInCallback {
  @ViewChild('placeHolderDraftName') placeHolderDraftName;
  @ViewChild('resourceComponent') resourceComponent;
  @ViewChild('draftBodyComponent') draftBodyComponent: ProjectDraftBodyComponent;
  @Input() draft: Draft;
  @Input() displayCover: boolean;
  @Input() editingDetails: string;
  @Input() displaySettings: boolean;
  @Output() editingDetailsChange = new EventEmitter<string>();
  @Output() displaySettingsChange = new EventEmitter<boolean>();
  @Output() displayCoverChange = new EventEmitter<boolean>();


  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();

  showDetailsVerticallyWithText: boolean = true;
  editingTitle: boolean = false;

  // draft: Draft;

  public renameDraft: boolean = false;
  sizeWidth: 0;

  trimSizePresets = TRIM_SIZE_PRESETS;
  selectedTrimSize: string = '';
  customWidth: number = 0;
  customHeight: number = 0;

  // ── Language combo box support ─────────────────────────────
  // See src/model/Languages.ts and ProjectBookComponent for the
  // parallel helpers used on the Book/Product side.
  languages: LanguageEntry[] = LANGUAGES;
  languageFilter: string = '';

  languageDisplayName(entry: LanguageEntry): string {
    if (!entry) { return ''; }
    return entry.translatable ? LanguagePipe.get(entry.name, 'compressSpaces') : entry.name;
  }

  filteredLanguages(): LanguageEntry[] {
    const f = (this.languageFilter || '').toString().trim().toLowerCase();
    if (!f) { return this.languages; }
    return this.languages.filter(l =>
      l.iso639_3.toLowerCase().indexOf(f) === 0 ||
      (l.iso639_2 && l.iso639_2.toLowerCase().indexOf(f) === 0) ||
      this.languageDisplayName(l).toLowerCase().indexOf(f) !== -1
    );
  }

  onDraftLanguageChange(value: string): void {
    const normalized = (value || '').toString().trim().toLowerCase();
    this.languageFilter = normalized;
    if (this.draft) { this.draft.languageCode = normalized; }
  }

  // Custom trim edit mode
  editingCustomTrim: boolean = false;
  editCustomWidth: number = 0;
  editCustomHeight: number = 0;

  // File upload state
  public hasBaseDropZoneOver: boolean = false;
  uploadingFile: File = null;
  uploadState: 'IDLE' | 'UPLOADING' | 'SUCCESS' | 'ERROR' = 'IDLE';
  uploadProgress: number = 0;
  uploadMessage: string = '';
  uploadError: string = '';

  /**
   * The largest `upload.maxBytes` the server has quoted us this session.
   *
   * Purely an optimisation, and deliberately NOT a limit. The real limit
   * arrives with each ticket and is the only value ever enforced; this copy
   * exists so a second attempt with the same oversized file can be refused
   * *before* calling the server. That matters now that tickets are rationed:
   * asking for a ticket creates a Resource row and spends one of the
   * organisation's 40 per hour, so letting a user retry a 2 GB file five
   * times would cost five of them for uploads that cannot succeed.
   *
   * Static so it survives navigation between drafts. Zero means "not known
   * yet", in which case nothing is pre-checked and the server decides.
   */
  private static lastKnownMaxBytes: number = 0;

  // Additional Settings collapse state
  showAdditionalSettings: boolean = false;

  // Stored paperType_ID per interiorType for switching back
  private storedGrayPaperTypeId: number = null;
  private storedColorPaperTypeId: number = null;

  /**
   * The binding / interior the Draft had BEFORE the edit in progress.
   *
   * `mat-button-toggle-group` writes through `[(ngModel)]` before it emits
   * `(change)`, so by the time a handler runs `draft.bindingType` is already
   * the new value and the old one is gone. The partition prompt needs both:
   * "was this Draft in step with its Project a moment ago?" is the whole
   * difference between an edit that split a Project and one that merely
   * re-stated a mismatch that was already there.
   *
   * Re-seeded whenever the `draft` input changes - otherwise navigating from
   * a COIL Draft to a PERFECT one would compare against the previous Draft.
   */
  private previousBindingType: string = '';
  private previousInteriorType: string = '';


  constructor(public router: Router, public startupService: StartupService, public changeDetectorRef: ChangeDetectorRef, public media: MediaMatcher, private draftService: DraftService, public draftBodyService: DraftBodyService, private draftCoverService: DraftCoverService, private resourceService: ResourceService, private route: ActivatedRoute, private projectService: ProjectService, public dialog: MatDialog, @Inject(LOCALE_ID) localeId: string) {
    this.startupService.touchLastUserActivity();
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);
    this.ensureDraftSidesLoaded();
    this.initTrimSizeSelection();
    this.rememberPartitionValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ( changes['draft'] ) {
      this.ensureDraftSidesLoaded();
      this.initTrimSizeSelection();
      this.rememberPartitionValues();
    }
  }

  // ─────────────────────────────────────────────────────────
  // Draft sides (DraftBody / DraftCover)
  //
  // `draft.draftBody` / `draft.draftCover` are OBJECT links, resolved
  // by `DraftService.getLinkData()` from `body_ID` / `cover_ID`
  // against the DraftBody / DraftCover service caches. They resolve
  // for a Draft that arrived from `GET /draft`, because that query
  // INNER JOINs both sides and the nested objects come down with it.
  //
  // They do NOT resolve for a Draft that arrived over
  // NotificationsService: that payload is built by `Drafts.one()`,
  // which loads no relations, so only the two ids are present. The
  // link is registered as a pending "attach request" that completes
  // when the DraftBody turns up in its own service — and for a
  // brand-new Draft it never does, because nothing pushes DraftBody
  // or DraftCover rows and neither has a create notification.
  //
  // The visible result was severe and easy to misread: the editor
  // template is gated on `*ngIf="draft && draft.draftBody"`, so a
  // freshly created Draft opened to a BLANK pane, and "Upload Cover
  // PDF" threw `Cannot set property 'resource_ID' of undefined`
  // inside an rxjs callback — no dialog, no message, nothing added.
  // That is the "the file dialog closes and no resource is added"
  // report.
  //
  // Fetching the two rows by id closes the gap. `getItem` routes
  // through `addUpdateLocalItem`, which resolves the pending attach
  // request, so assigning here is belt-and-braces rather than the
  // mechanism.
  // ─────────────────────────────────────────────────────────

  /** Ids already requested, so template-driven re-entry cannot loop. */
  private requestedSideIds = new Set<string>();

  private ensureDraftSidesLoaded(): void {
    if ( !this.draft ) {
      return;
    }

    if ( !this.draft.draftBody && this.draft.body_ID ) {
      const key = 'body:' + this.draft.body_ID;
      if ( !this.requestedSideIds.has(key) ) {
        this.requestedSideIds.add(key);
        this.draftBodyService.getItem(this.draft.body_ID).subscribe(
          body => {
            if ( body && this.draft && !this.draft.draftBody ) {
              this.draft.draftBody = body;
            }
            this.initTrimSizeSelection();
            try { this.changeDetectorRef.detectChanges(); } catch ( e ) { /* view may be gone */ }
          },
          err => console.error(`Could not load DraftBody ${this.draft.body_ID}`, err)
        );
      }
    }

    if ( !this.draft.draftCover && this.draft.cover_ID ) {
      const key = 'cover:' + this.draft.cover_ID;
      if ( !this.requestedSideIds.has(key) ) {
        this.requestedSideIds.add(key);
        this.draftCoverService.getItem(this.draft.cover_ID).subscribe(
          cover => {
            if ( cover && this.draft && !this.draft.draftCover ) {
              this.draft.draftCover = cover;
            }
            try { this.changeDetectorRef.detectChanges(); } catch ( e ) { /* view may be gone */ }
          },
          err => console.error(`Could not load DraftCover ${this.draft.cover_ID}`, err)
        );
      }
    }
  }

  /** Determine which preset matches the current draft body dimensions, or set to Custom */
  initTrimSizeSelection() {
    if (!this.draft || !this.draft.draftBody) { return; }
    const widthIn = this.draft.draftBody.width / 72;
    const heightIn = this.draft.draftBody.height / 72;
    const match = this.trimSizePresets.find(p =>
      Math.abs(p.width - widthIn) < 0.01 && Math.abs(p.height - heightIn) < 0.01
    );
    if (match) {
      this.selectedTrimSize = match.label;
    } else {
      this.selectedTrimSize = 'CUSTOM';
      this.customWidth = parseFloat(widthIn.toFixed(4));
      this.customHeight = parseFloat(heightIn.toFixed(4));
    }
  }

  /** Called when the user picks a preset or "Custom" from the combo box */
  onTrimSizePresetChange(value: string) {
    if (value === 'CUSTOM') {
      // Enter edit mode with current values
      this.selectedTrimSize = 'CUSTOM';
      this.editCustomWidth = parseFloat((this.draft.draftBody.width / 72).toFixed(4));
      this.editCustomHeight = parseFloat((this.draft.draftBody.height / 72).toFixed(4));
      this.editingCustomTrim = true;
    } else {
      const preset = this.trimSizePresets.find(p => p.label === value);
      if (preset) {
        this.selectedTrimSize = value;
        this.editingCustomTrim = false;
        this.draft.draftBody.width = preset.width * 72;
        this.draft.draftBody.height = preset.height * 72;
        this.customWidth = preset.width;
        this.customHeight = preset.height;
        this.draftBodyService.updateItem(this.draft.draftBody).subscribe();
      }
    }
  }

  /** True when the edit values differ from the saved custom values */
  get customTrimChanged(): boolean {
    return this.editCustomWidth !== this.customWidth || this.editCustomHeight !== this.customHeight;
  }

  /** Save the custom trim values and exit edit mode */
  saveCustomTrim(): void {
    if (this.editCustomWidth > 0 && this.editCustomHeight > 0) {
      this.customWidth = this.editCustomWidth;
      this.customHeight = this.editCustomHeight;
      this.draft.draftBody.width = this.customWidth * 72;
      this.draft.draftBody.height = this.customHeight * 72;
      this.draftBodyService.updateItem(this.draft.draftBody).subscribe();
      this.editingCustomTrim = false;
    }
  }

  /** Cancel custom trim editing and revert to saved values */
  cancelCustomTrim(): void {
    this.editingCustomTrim = false;
    // If we were already custom, stay custom; otherwise revert to the previous preset
    this.initTrimSizeSelection();
  }

  /** Called when custom width or height input changes */
  onCustomTrimSizeChange() {
    if (this.customWidth > 0 && this.customHeight > 0) {
      this.draft.draftBody.width = this.customWidth * 72;
      this.draft.draftBody.height = this.customHeight * 72;
      this.draftBodyService.updateItem(this.draft.draftBody).subscribe();
    }
  }

  onDataChange() {
    this.editingDetailsChange.emit(this.editingDetails);
    this.displaySettingsChange.emit(this.displaySettings);
    this.displayCoverChange.emit(this.displayCover);
  }

  startEditTitle() {
    this.editingTitle = true;
    setTimeout(() => {
      const input = document.querySelector('.inline-title-input') as HTMLInputElement;
      if (input) { input.focus(); input.select(); }
    });
  }

  commitTitle(value: string) {
    this.editingTitle = false;
    const trimmed = value.trim();
    if (trimmed && trimmed !== this.draft.title) {
      this.draft.title = trimmed;
      this.draftService.updateItem(this.draft).subscribe();
    }
  }

  cancelTitle(input: HTMLInputElement) {
    input.value = this.draft.title || '';
    this.editingTitle = false;
  }

  /** Handle switching between GRAY and COLOR interiorType, storing/restoring paperType_ID */
  onInteriorTypeChange(newValue: string): void {
    // Store the current paperType_ID for the previous interiorType
    if (newValue === 'COLOR') {
      this.storedGrayPaperTypeId = this.draft.draftBody.paperType_ID;
      this.draft.draftBody.paperType_ID = this.storedColorPaperTypeId != null ? this.storedColorPaperTypeId : 5;
    } else {
      this.storedColorPaperTypeId = this.draft.draftBody.paperType_ID;
      this.draft.draftBody.paperType_ID = this.storedGrayPaperTypeId != null ? this.storedGrayPaperTypeId : 1;
    }
    // The paper stock moved with the interior, so BOTH rows need saving.
    this.saveDraftBody();
    this.commitPartitionChange('interiorType', newValue);
  }

  /** Handle switching between PERFECT and COIL bindingType. */
  onBindingTypeChange(newValue: string): void {
    this.commitPartitionChange('bindingType', newValue);
  }

  // ── Persisting the Book Settings panel ───────────────────────────────
  //
  // Every control in that panel is bound with `[(ngModel)]`, or writes the
  // model directly from a `(change)` expression. Both mutate the in-memory
  // object and nothing else, so until these handlers existed the ENTIRE
  // panel - subtitle, author, note, language, paper stock, lamination, ICC
  // and cover colour boost - was silently discarded the moment the object
  // was re-read from the server. No error, no warning, and the values stayed
  // on screen for the rest of the session, which is why it was not obvious.
  //
  // Text fields save on `(change)` rather than `(ngModelChange)` on purpose:
  // the native change event fires on blur, and only when the value actually
  // differs, so typing a 40-character subtitle costs ONE request instead of
  // forty. The language autocomplete is the exception - its
  // `(ngModelChange)` runs on every keystroke, so it saves on blur and on
  // option-select instead.

  /** Persist the Draft row (title, subtitle, author, note, language, binding, interior). */
  saveDraft(): void {
    if (this.draft) {
      this.draftService.updateItem(this.draft).subscribe();
    }
  }

  /** Persist the DraftBody row (paper stock, ICC, trim). */
  saveDraftBody(): void {
    if (this.draft && this.draft.draftBody) {
      this.draftBodyService.updateItem(this.draft.draftBody).subscribe();
    }
  }

  /** Persist the DraftCover row (lamination, colour boost). */
  saveDraftCover(): void {
    if (this.draft && this.draft.draftCover) {
      this.draftCoverService.updateItem(this.draft.draftCover).subscribe();
    }
  }

  /**
   * Persist a binding / interior edit, then ask the user about it if it just
   * split the Draft's Project.
   *
   * The save is not incidental. These two toggles - along with the rest of
   * the Book Settings panel - mutated the in-memory Draft through
   * `[(ngModel)]` and were never written back, so the change survived only
   * until the object was re-read. Prompting the user to move a Draft on the
   * strength of an unsaved value would have been worse than not prompting at
   * all: answering "move" would relocate the Draft and the binding change
   * that justified the move would then be lost.
   *
   * (The remaining fields in that panel - subtitle, author, note, language,
   * lamination, cover colour boost - have the same gap and are NOT fixed
   * here. They are a separate defect; see the session notes.)
   */
  private commitPartitionChange(property: PartitionProperty, newValue: string): void {
    if (!this.draft) {
      return;
    }
    const previousValue = property === 'bindingType' ? this.previousBindingType : this.previousInteriorType;
    this.saveDraft();
    this.rememberPartitionValues();

    const project = this.draft.project;
    const decision = evaluatePartitionPrompt(
      property,
      previousValue,
      newValue,
      project ? this.otherProjectMembers(project) : null
    );
    if (!decision.prompt) {
      return;
    }

    const dialogConfig = modernDialogConfig({ width: '520px' });
    const data: DraftPartitionPromptData = {
      projectName: project.name,
      property: property,
      newValue: decision.newValue,
      projectValue: decision.projectValue
    };
    dialogConfig.data = data;
    this.dialog.open(DraftPartitionPromptComponent, dialogConfig).afterClosed()
      .subscribe((result: DraftPartitionPromptResult) => {
        // Dismissed (Esc / backdrop) is NOT an answer - leave the Draft where
        // it is and record nothing. See the dialog's class comment.
        if (!result) {
          return;
        }
        if (result === 'MOVE') {
          // Same call the thumbnail menus use. The server moves the Draft out
          // of its current Project and into a brand new one; `projectName` is
          // base64-encoded inside ProjectEndpoints, so the raw title goes here.
          this.projectService.createNewProjectWithDraft(this.draft.ID, this.draft.title).subscribe();
        }
        this.markProjectEdited(project);
      });
  }

  /**
   * Every OTHER active Book and Draft in the Project.
   *
   * `project.drafts` and `project.books` are populated by the reverse
   * `work_ID` links in `DraftService` / `BookService.getLinkData()`, NOT by
   * the Project payload - `GET /project` nests nothing.
   *
   * One consequence to know about: a Book only reaches the browser nested
   * under `product.book`, so a Book with no Product is invisible here and
   * `project.books` is a LOWER BOUND rather than the full membership. The
   * effect on this prompt is conservative in the right direction - an unseen
   * member can only cause a missed question, never a spurious one - and it
   * resolves itself when the paginated Book list call lands.
   */
  private otherProjectMembers(project: any): PartitionMember[] {
    const members: PartitionMember[] = [];
    for (const sibling of (project.drafts || [])) {
      if (sibling && sibling.ID !== this.draft.ID) {
        members.push(sibling);
      }
    }
    for (const book of (project.books || [])) {
      if (book) {
        members.push(book);
      }
    }
    return members;
  }

  /**
   * Record that a human has inspected this Project, so the server-side
   * reconciler stops re-deciding it.
   *
   * Guarded on the field already existing on the object. `mappingStatus` is
   * part of the Project-backfill work the server has not shipped yet, and the
   * model's `update()` only copies properties the server actually sends - so
   * until it does, this is `undefined` and nothing is written. Once the
   * server starts sending it, this starts working with no further change
   * here. Writing it unconditionally would PUT a field the server does not
   * know about yet.
   */
  private markProjectEdited(project: any): void {
    if (!project || project.mappingStatus === undefined) {
      return;
    }
    project.mappingStatus = 'EDITED';
    this.projectService.updateItem(project).subscribe();
  }

  /** Re-seed the pre-edit binding / interior values from the current Draft. */
  private rememberPartitionValues(): void {
    this.previousBindingType = this.draft ? this.draft.bindingType : '';
    this.previousInteriorType = this.draft ? this.draft.interiorType : '';
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      console.log("Not logged in in Account");
      this.router.navigate(['/home/login']);
    } else {
      this.startupService.touchLastUserActivity();
      // this.getDraft();
    }
  }

  onSubmit() {

  }
  // getDraft() {
  //   this.startupService.touchLastUserActivity();
  //   const draftId = this.route.snapshot.paramMap.get('id');
  //   this.draftService.getDraft(draftId).subscribe( draft => {
  //     this.draft = draft;
  //   }, err => {
  //     this.router.navigate(['/home/login']);
  //   });
  // }

  onResize(event: any) {
    console.log("onresize " + event);
    const w = event.target.innerWidth;
    this.sizeWidth = w;
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  // --- Download Original PDFs ---
  //
  // Parallel to the equivalent handlers in `ProjectBookComponent`
  // — resolve a pre-signed download URL for the underlying
  // `Resource` and open it in a new tab. Filename is derived
  // from the Draft title with a `_original_cover.pdf` /
  // `_original_body.pdf` suffix so downloads are self-describing
  // when they land in the operator's Downloads folder. Guarded
  // on the Draft / Cover / Body / resource_ID chain so the
  // buttons never fire on an incomplete Draft (they're also
  // `*ngIf`-hidden in the template for the same reason, but the
  // TS guards keep the behaviour correct if a caller wires the
  // buttons up differently in the future).

  downloadOriginalCover(): void {
    if (!this.draft || !this.draft.draftCover || !this.draft.draftCover.resource_ID) {
      return;
    }
    const filename = (this.draft.title || 'cover') + '_original_cover.pdf';
    this.resourceService.getOriginalURL(this.draft.draftCover.resource_ID, filename).subscribe((url: any) => {
      if (url) {
        window.open(url, '_blank');
      }
    });
  }

  downloadOriginalBody(): void {
    if (!this.draft || !this.draft.draftBody || !this.draft.draftBody.resource_ID) {
      return;
    }
    const filename = (this.draft.title || 'body') + '_original_body.pdf';
    this.resourceService.getOriginalURL(this.draft.draftBody.resource_ID, filename).subscribe((url: any) => {
      if (url) {
        window.open(url, '_blank');
      }
    });
  }

  // --- Resource preparation problems ---
  //
  // DraftCover / DraftBody carry only `resource_ID` — unlike Cover / Body on
  // the Book side, they have no nested `resource` object — so the Resource has
  // to be looked up in ResourceService and fetched on demand if it is not
  // cached yet. ResourceService is not one of the services loaded at startup.
  //
  // `requestedResources` is what stops that fetch becoming a request storm:
  // these getters are called from the template, so Angular re-evaluates them on
  // every change-detection pass, and an unguarded `getItem()` would fire one
  // HTTP request per tick for as long as the row stayed uncached. Same guard as
  // ProjectDraftThumbnailComponent.getResourceStatusColor().
  private requestedResources: Set<number> = new Set<number>();

  private resourceFor(resource_ID: number): Resource {
    if (!resource_ID) {
      return null;
    }
    const res = this.resourceService.objects.find(r => r.ID === resource_ID);
    if (!res) {
      if (!this.requestedResources.has(resource_ID)) {
        this.requestedResources.add(resource_ID);
        this.resourceService.getItem(resource_ID).subscribe();
      }
      return null;
    }
    return res;
  }

  get coverResource(): Resource {
    return this.resourceFor(this.draft && this.draft.draftCover ? this.draft.draftCover.resource_ID : 0);
  }

  get bodyResource(): Resource {
    return this.resourceFor(this.draft && this.draft.draftBody ? this.draft.draftBody.resource_ID : 0);
  }

  get coverHasError(): boolean {
    const r = this.coverResource;
    return !!r && r.prepared === 'ERROR';
  }

  get bodyHasError(): boolean {
    const r = this.bodyResource;
    return !!r && r.prepared === 'ERROR';
  }

  /** True when either resource failed preparation, so the panel should render. */
  get hasResourceProblems(): boolean {
    return this.coverHasError || this.bodyHasError
      || !!(this.coverResource && this.coverResource.problemNote)
      || !!(this.bodyResource && this.bodyResource.problemNote);
  }

  // ── File status: preparation ───────────────────────────────
  //
  // WHY THERE IS NO "STANDARDIZE" CONTROL HERE
  // ------------------------------------------
  // `standardized` is a column on the `body` / `cover` tables — the
  // Book side. A Draft's `draftBody` / `draftCover` are rows in
  // `mutablebody` / `mutablecover`, which have no such column, and the
  // server entities do not declare one. Every server-side query that
  // moves a row through standardization joins `body` / `cover` to
  // `book`. So at the Draft stage there is nothing for a standardize
  // button to set and nothing that would ever read it — a button here
  // would write a property that TypeORM drops on the floor and no
  // worker would ever see, i.e. it would look like it worked and do
  // nothing.
  //
  // Standardization is not SKIPPED for a Draft, it is DEFERRED.
  // `Projects.makeDraftIntoBook()` copies the Draft's settings onto a
  // new `Body` / `Cover`, carries the SAME `resource_ID` across, and
  // sets `standardized = 'WAITING'` itself. The work is queued the
  // instant the Draft becomes a Book, against the very PDF being
  // prepared here. The note at the bottom of the panel says that,
  // which is the honest version of the button this view might
  // otherwise look like it is missing.
  //
  // PREPARATION, by contrast, genuinely IS a Draft-stage concern.
  // `Resource.prepared` lives on `resource`, which both sides share,
  // and the job creator selects on `resource.prepared = 'WAITING' AND
  // resource.type = 'PDF'` with no join to `book`. Kicking it from
  // here works exactly as it does on the Book side.

  /** The `Resource` behind a DraftCover / DraftBody, or null. */
  private sideResource(side: any): Resource {
    return this.resourceFor(side && side.resource_ID);
  }

  /** Badge fill colour, shared with the Book view and the thumbnails. */
  getResourceStatusColor(side: any): string {
    return ResourceStatus.fill(!!(side && side.resource_ID), this.sideResource(side));
  }

  getResourceDisplayString(side: any): string {
    return ResourceStatus.displayString(side && side.resource_ID);
  }

  /**
   * Hover text for the badge. `standardized` is deliberately omitted —
   * it does not exist for a Draft, and `ResourceStatus.hoverText`
   * already treats a missing value as "nothing to report" rather than
   * inventing a status.
   */
  getResourceHoverText(side: any, label: string): string {
    return ResourceStatus.hoverText(label, !!(side && side.resource_ID), this.sideResource(side));
  }

  isResourceActionable(side: any): boolean {
    return ResourceStatus.isActionable(this.sideResource(side));
  }

  /** Raw `prepared` value, or '' when there is no resource to report on. */
  preparedStatus(side: any): string {
    if (!side || !side.resource_ID) { return ''; }
    const res = this.sideResource(side);
    // Resource row not back from the server yet — say so rather than
    // implying the pipeline has not started.
    return res ? (res.prepared || 'UNKNOWN') : 'UNKNOWN';
  }

  preparedStatusLabel(side: any): string {
    return ResourceStatus.statusLabel(this.preparedStatus(side));
  }

  /** Same class vocabulary as ProjectBookComponent.getStatusClass. */
  getStatusClass(status: string): string {
    switch (status) {
      case 'INITIAL': return 'status-initial';
      case 'WAITING': return 'status-waiting';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'COMPLETE': return 'status-complete';
      case 'ERROR': return 'status-error';
      default: return 'status-unknown';
    }
  }

  /**
   * Preparation may be requested only from INITIAL. WAITING /
   * IN_PROGRESS are already queued, COMPLETE is done, and ERROR needs
   * the upload itself fixed before a retry could succeed — offering
   * the button there invites the user to re-run a step that cannot
   * work yet.
   */
  private canPrepare(side: any): boolean {
    const res = this.sideResource(side);
    return !!res && res.prepared === 'INITIAL';
  }

  get canPrepareCover(): boolean {
    return this.canPrepare(this.draft && this.draft.draftCover);
  }

  get canPrepareBody(): boolean {
    return this.canPrepare(this.draft && this.draft.draftBody);
  }

  /** True when at least one side has a PDF whose preparation can be started. */
  get canProcessFiles(): boolean {
    return this.canPrepareCover || this.canPrepareBody;
  }

  /** True while either side is queued for or undergoing preparation. */
  get filesPreparing(): boolean {
    const cr = this.coverResource;
    const br = this.bodyResource;
    const busy = (r: Resource) => !!r && (r.prepared === 'WAITING' || r.prepared === 'IN_PROGRESS');
    return busy(cr) || busy(br);
  }

  /** True when the panel has something to report at all. */
  get hasAnyResource(): boolean {
    return !!(this.draft
      && ((this.draft.draftCover && this.draft.draftCover.resource_ID)
       || (this.draft.draftBody && this.draft.draftBody.resource_ID)));
  }

  /**
   * True when every PDF the Draft actually has is prepared. This is
   * the state where nothing further can be done at the Draft stage,
   * and therefore the moment to explain that standardization happens
   * later, when the Draft is made into a Book.
   */
  get filesPrepared(): boolean {
    if (!this.hasAnyResource) { return false; }
    const done = (side: any) => {
      if (!side || !side.resource_ID) { return true; }   // nothing to prepare
      const r = this.sideResource(side);
      return !!r && r.prepared === 'COMPLETE';
    };
    return done(this.draft.draftCover) && done(this.draft.draftBody);
  }

  /**
   * Start preparation for whichever side is ready for it. Mirrors the
   * prepare half of `ProjectBookComponent.processPdfFiles()`; there is
   * no standardize half at the Draft stage (see the note above).
   */
  processPdfFiles(): void {
    if (!this.draft) { return; }
    for (const side of [this.draft.draftCover, this.draft.draftBody]) {
      if (!this.canPrepare(side)) { continue; }
      const res = this.sideResource(side);
      res.prepared = 'WAITING';
      this.resourceService.updateItem(res).subscribe();
    }
  }

  /**
   * Badge click — advances the one stage a Draft resource has.
   * Delegates the decision to `ResourceStatus.action` so this view,
   * the Book view and the three thumbnails cannot disagree about when
   * the badge is actionable.
   */
  onResourceBadgeClick(side: any, event: Event): void {
    if (event) { event.stopPropagation(); }
    if (!side) { return; }
    const res = this.sideResource(side);
    if (ResourceStatus.action(res) !== 'PREPARE') { return; }
    res.prepared = 'WAITING';
    this.resourceService.updateItem(res).subscribe();
  }

  isDraftNameUsed(): boolean {
    if ( !this.placeHolderDraftName || !this.placeHolderDraftName.nativeElement || !this.placeHolderDraftName.nativeElement.value ) {
      return true;
    }
    const newDraftName = this.placeHolderDraftName.nativeElement.value.trim();
    for ( const draft of this.startupService.drafts ) {
      if ( newDraftName === draft.title ) {
        return true;
      }
    }
    return false;
  }
  saveDraftValue() {
    console.log('Save Draft Name' + this.placeHolderDraftName.nativeElement.value);
    this.draft.title = this.placeHolderDraftName.nativeElement.value;
    this.draftService.updateItem(this.draft);
    // this.projectService.saveProjectImmediately(this.project).then( proj => {
    //   console.log('Updated project name');
    // });
  }
  cancelProjectValue() {
    this.placeHolderDraftName.nativeElement.value = this.draft.title;
  }

  /** Handle cover shift changes from the floating toolbar */
  onCoverShiftChanged(event: { verticalShift: number, horizontalShift: number }): void {
    this.draft.draftCover.verticalShift = event.verticalShift;
    this.draft.draftCover.horizontalShift = event.horizontalShift;
    this.draftCoverService.updateItem(this.draft.draftCover).subscribe();
  }

  /** Handle cover scale changes from the floating toolbar */
  onCoverScaleChanged(newScale: number): void {
    this.draft.draftCover.documentScale = newScale;
    this.draftCoverService.updateItem(this.draft.draftCover).subscribe();
  }

  /** Handle trim size changes from the cover floating toolbar */
  onCoverTrimChanged(event: { width: number, height: number }): void {
    this.draft.draftBody.width = event.width;
    this.draft.draftBody.height = event.height;
    // Reset cover shifts to zero so the cover is re-centered in the new trim area
    this.draft.draftCover.horizontalShift = 0;
    this.draft.draftCover.verticalShift = 0;
    this.initTrimSizeSelection();
    // Force change detection so the resource component picks up the new shift values
    this.changeDetectorRef.detectChanges();
    // Explicitly trigger refresh on the resource component to recompute positions
    if (this.resourceComponent) {
      setTimeout(() => {
        this.resourceComponent.refreshImagePosition();
      }, 100);
    }
    this.draftBodyService.updateItem(this.draft.draftBody).subscribe();
    this.draftCoverService.updateItem(this.draft.draftCover).subscribe();
  }

  /** File upload handlers */
  fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  onFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (file) {
      this.handleFileUpload(file);
    }
    // Reset the input so the same file can be re-selected
    event.target.value = '';
  }

  onFileDrop(event: DragEvent): void {
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  /** Clear the upload state so the user can select another file */
  clearUpload(): void {
    this.uploadingFile = null;
    this.uploadState = 'IDLE';
    this.uploadProgress = 0;
    this.uploadMessage = '';
    this.uploadError = '';
  }

  /**
   * Upload flow:
   * 1. Call the server to create a Resource and get a presigned POST ticket
   * 2. Refuse the file here if it is over the ticket's `maxBytes`
   * 3. Assign the new Resource.ID to DraftBody.resource_ID or DraftCover.resource_ID
   * 4. POST the PDF to S3 as multipart/form-data via XHR (with progress tracking)
   * 5. On success, save the DraftBody or DraftCover via its respective service
   *
   * Step 1 is no longer free. It creates a row AND spends one of the
   * organisation's upload tickets — 40 per rolling hour, which is roughly 20
   * books once a body and a cover are counted separately. So every failure
   * path below says what actually happened instead of offering a retry that
   * would spend another ticket to fail the same way.
   */
  private handleFileUpload(file: File): void {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.uploadError = LanguagePipe.get('Only PDF files are accepted.', 'compressSpaces');
      this.uploadState = 'ERROR';
      return;
    }

    const isCover = this.displayCover;

    // The side has to exist BEFORE the upload starts. Assigning
    // `resource_ID` onto an undefined `draftCover` throws inside the
    // rxjs success callback, where it produces no dialog and no
    // message — the upload appears to do nothing at all. See
    // `ensureDraftSidesLoaded` for why the side can be missing.
    const side = isCover ? this.draft && this.draft.draftCover : this.draft && this.draft.draftBody;
    if (!side) {
      this.ensureDraftSidesLoaded();
      this.uploadingFile = file;
      this.uploadState = 'ERROR';
      this.uploadError = LanguagePipe.get('This Draft is still loading. Please try again in a moment.', 'compressSpaces');
      console.error('handleFileUpload: draft side not loaded',
        { isCover: isCover, body_ID: this.draft && this.draft.body_ID, cover_ID: this.draft && this.draft.cover_ID });
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Reset and show the file item
    this.uploadingFile = file;
    this.uploadState = 'UPLOADING';
    this.uploadProgress = 0;
    this.uploadMessage = '';
    this.uploadError = '';

    // Cheap pre-check against the last limit the server quoted. Only ever
    // rejects a file the server has already told us is too big for the bucket
    // policy, and only once we have been told a number at all — the
    // authoritative check is against this upload's own ticket, below.
    if (ProjectDraftComponent.lastKnownMaxBytes > 0 && file.size > ProjectDraftComponent.lastKnownMaxBytes) {
      this.uploadState = 'ERROR';
      this.uploadError = this.tooLargeMessage(ProjectDraftComponent.lastKnownMaxBytes);
      this.changeDetectorRef.detectChanges();
      return;
    }

    // `type` is the resource FORMAT, not BODY-vs-COVER (the server column is
    // varchar(6) NOT NULL and holds 'PDF', 'XPS', ...). It was never set, so
    // every uploaded resource landed with type ''. That is not just untidy:
    // `Resources.getImageURL` / `getCroppedImageURL` both gate on
    // `resource.type === 'PDF'` before rendering, so a '' resource silently
    // never produced a preview image for the draft body or cover.
    // We only accept .pdf above, so 'PDF' is the correct value here.
    const newResource = new Resource({ org_ID: this.draft.org_ID, type: 'PDF' });

    // Step 1: create the Resource and get the presigned POST ticket
    this.resourceService.getUploadUrl(newResource).subscribe(
      (result) => {
        if (!result || !result.resource) {
          this.uploadState = 'ERROR';
          this.uploadError = LanguagePipe.get('Failed to obtain upload URL from server.', 'compressSpaces');
          this.changeDetectorRef.detectChanges();
          return;
        }

        const ticket = result.upload;
        if (!ticket) {
          // The server answered but without an `upload` object. `uploadURL`
          // was deleted server-side on 2026-09-14 and there is deliberately no
          // fallback to it — see `ResourceService.getUploadUrl`. Report it.
          this.uploadState = 'ERROR';
          this.uploadError = LanguagePipe.get('Failed to obtain upload URL from server.', 'compressSpaces');
          console.error('getUploadUrl: response carried no upload ticket', result);
          this.changeDetectorRef.detectChanges();
          return;
        }
        ProjectDraftComponent.lastKnownMaxBytes = ticket.maxBytes || ProjectDraftComponent.lastKnownMaxBytes;

        // Step 2: size check BEFORE sending a gigabyte at S3. Without this the
        // user's reward for a too-large file is an `EntityTooLarge` XML
        // document the browser shows as a blank failure, after uploading the
        // whole thing.
        if (exceedsMaxBytes(file.size, ticket)) {
          this.uploadState = 'ERROR';
          this.uploadError = this.tooLargeMessage(ticket.maxBytes);
          this.changeDetectorRef.detectChanges();
          return;
        }

        // Step 3: Assign the Resource.ID immediately
        if (isCover) {
          this.draft.draftCover.resource_ID = result.resource.ID;
        } else {
          this.draft.draftBody.resource_ID = result.resource.ID;
        }

        // Step 4: upload the file to S3 with XHR for progress
        this.uploadToS3WithProgress(ticket, file, isCover);
      },
      (error) => {
        console.error('Error getting upload URL:', error);
        this.uploadState = 'ERROR';
        if (isRateLimited(error)) {
          // The organisation has spent its upload allowance for this rolling
          // hour. NOT retried, here or anywhere — the server asked for that
          // explicitly. A 429 costs no ticket, so the only cost of the user
          // trying again too early is another 429.
          this.uploadError = this.rateLimitMessage(error);
        } else if (error && error.status === 400) {
          // A 400 here is the server refusing to create the Resource —
          // `Resources.add` only accepts an `organization` role whose id
          // matches `org_ID`. Saying so beats a generic retry prompt,
          // because retrying will fail identically.
          this.uploadError = LanguagePipe.get('The server would not accept the file for this organization.', 'compressSpaces');
        } else {
          this.uploadError = LanguagePipe.get('Failed to upload', 'compressSpaces') + ` ${file.name}. ` + LanguagePipe.get('Please try again.', 'compressSpaces');
        }
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  /** "This file is too large to upload. The maximum size is 1 GB." */
  private tooLargeMessage(maxBytes: number): string {
    const size = formatBytes(maxBytes);
    let message = LanguagePipe.get('This file is too large to upload.', 'compressSpaces');
    if (size) {
      message += ' ' + LanguagePipe.get('The maximum size is', 'compressSpaces')
        + ' ' + LanguagePipe.localizeNumber(size) + '.';
    }
    return message;
  }

  /**
   * The 429 sentence, built from the body rather than from a literal.
   *
   * `limit` is environment configuration (`ResourceUploadUrlsPerOrgPerHour`)
   * and the server expects to raise it per customer, so a hardcoded number
   * here would eventually tell the user something untrue. The server's own
   * English goes to the console; the user gets the translated version.
   *
   * ⚠ The retry time comes from `retryAfterSeconds`, **never** from
   * `windowSeconds`. The window ROLLS — the allowance returns as each ticket
   * ages out, so the real wait is usually seconds or a few minutes even though
   * the window is a constant 3600. Building the sentence from `windowSeconds`
   * would tell every refused user to come back in an hour, which is exactly
   * the defect the server fixed on 2026-09-14. Reintroducing it here would be
   * worse than their original, because their number is now correct.
   */
  private rateLimitMessage(error: any): string {
    const info = parseRateLimit(error);
    if (info.message) {
      console.error('Resource upload rate limited:', info.message, info);
    }

    let message = LanguagePipe.get('Upload limit reached for this organization.', 'compressSpaces');
    if (!isNaN(info.limit)) {
      message += ' ' + LanguagePipe.get('The limit is', 'compressSpaces')
        + ' ' + LanguagePipe.localizeNumber(info.limit)
        + ' ' + LanguagePipe.get('uploads per hour.', 'compressSpaces');
    }

    if (isNaN(info.retryAfterSeconds)) {
      // The server did not say. Do not invent a duration — promising a wait we
      // cannot support is worse than not naming one.
      message += ' ' + LanguagePipe.get('Please try again.', 'compressSpaces');
    } else if (info.retryAfterSeconds >= 120) {
      // Always two or more, so the plural is always right and no singular form
      // is needed in any of the 21 bundles.
      const minutes = Math.ceil(info.retryAfterSeconds / 60);
      message += ' ' + LanguagePipe.get('Please try again in', 'compressSpaces')
        + ' ' + LanguagePipe.localizeNumber(minutes)
        + ' ' + LanguagePipe.get('minutes.', 'compressSpaces');
    } else {
      message += ' ' + LanguagePipe.get('Please try again shortly.', 'compressSpaces');
    }
    return message;
  }

  /** Turn S3's XML refusal into a sentence. */
  private uploadFailureMessage(kind: UploadFailureKind, file: File, status: number, ticket: ResourceUploadTicket): string {
    switch (kind) {
      case 'TOO_LARGE':
        return this.tooLargeMessage(ticket ? ticket.maxBytes : 0);
      case 'EXPIRED':
        // The policy expiry is enforced when S3 RECEIVES the request, so this
        // never means "the transfer was too slow" — it means the POST began
        // after the hour was up, i.e. the file picker sat open. A fresh ticket
        // genuinely fixes it.
        return LanguagePipe.get('The upload link expired. Please try again.', 'compressSpaces');
      case 'REJECTED':
        // A policy condition failed, or the form was malformed. Both mean the
        // ticket's signed fields did not arrive as sent, which is our bug, not
        // the user's — and a retry reproduces it exactly.
        return LanguagePipe.get('The server rejected the upload.', 'compressSpaces')
          + ` (${LanguagePipe.localizeNumber(status)})`;
      default:
        return LanguagePipe.get('Failed to upload', 'compressSpaces')
          + ` ${file.name}. ` + LanguagePipe.get('Please try again.', 'compressSpaces');
    }
  }

  /**
   * POST the file to S3 as multipart/form-data, using XHR for progress.
   * On success, saves the DraftBody or DraftCover.
   *
   * ⚠ Three things here are load-bearing and look like noise:
   *   - the method is POST, not PUT, and the target is `ticket.url` (the
   *     bucket root) rather than an object URL. The presigned PUT is gone:
   *     `uploadURL` was deleted server-side on 2026-09-14 and `PUT` was
   *     removed from the bucket CORS rule at the same time.
   *   - the body is the FormData built by `buildUploadForm`, whose field
   *     order is the server's and whose file part is last;
   *   - **no `Content-Type` header is set.** The old PUT set
   *     `application/pdf` explicitly. Doing that here overwrites the
   *     `multipart/form-data; boundary=...` the browser generates, the
   *     boundary is lost, and S3 cannot parse the body at all.
   * Success is 204 No Content.
   */
  private uploadToS3WithProgress(ticket: ResourceUploadTicket, file: File, isCover: boolean): void {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable) {
        this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        this.changeDetectorRef.detectChanges();
      }
    };

    xhr.onload = () => {
      if (isUploadSuccess(xhr.status)) {
        this.uploadProgress = 100;
        // Step 5: Save the DraftBody or DraftCover
        const onSaveSuccess = () => {
          this.uploadState = 'SUCCESS';
          this.uploadMessage = `${file.name} ` + LanguagePipe.get('uploaded successfully!', 'compressSpaces');
          this.changeDetectorRef.detectChanges();
        };
        const onSaveError = (err) => {
          console.error('Error saving draft resource assignment:', err);
          this.uploadState = 'ERROR';
          this.uploadError = LanguagePipe.get('File uploaded but failed to save.', 'compressSpaces');
          this.changeDetectorRef.detectChanges();
        };

        if (isCover) {
          this.draftCoverService.updateItem(this.draft.draftCover).subscribe(onSaveSuccess, onSaveError);
        } else {
          this.draftBodyService.updateItem(this.draft.draftBody).subscribe(onSaveSuccess, onSaveError);
        }
      } else {
        // S3 answers a refused POST with an XML document, not JSON, and the
        // status alone does not separate "too big" from "the policy did not
        // match". `classifyUploadFailure` reads the <Code> element.
        const kind = classifyUploadFailure(xhr.status, xhr.responseText);
        console.error('S3 rejected the upload', { status: xhr.status, kind: kind, response: xhr.responseText });
        this.uploadState = 'ERROR';
        this.uploadError = this.uploadFailureMessage(kind, file, xhr.status, ticket);
        this.changeDetectorRef.detectChanges();
      }
    };

    xhr.onerror = () => {
      // ⚠ A network error on THIS POST does not mean the file is absent.
      //
      // A multipart/form-data POST is a CORS *simple request*, so there is no
      // preflight to fail. The server measured this against the live bucket on
      // 2026-09-14: from an origin the bucket rule does not list, S3 returned
      // 204 and **stored the object**, with no `Access-Control-Allow-Origin` —
      // so the browser blocks us from reading the response to an upload that
      // actually succeeded, and it arrives here as an opaque error.
      //
      // That is the opposite of the old PUT, which failed loudly on the
      // preflight before any bytes moved. So this must NOT invite a retry: a
      // retry would store a duplicate object and spend another of the 40
      // hourly tickets. The likely cause is a missing origin — a new language
      // subdomain or a new environment — and it is one `put-bucket-cors` on
      // the server side, which they have asked us to report rather than work
      // around.
      console.error('S3 upload: opaque network error. The object MAY have been stored. '
        + 'Most likely a missing origin in the bucket CORS rule - report it, do not retry.',
        { url: ticket.url, file: file.name, size: file.size });
      this.uploadState = 'ERROR';
      this.uploadError = LanguagePipe.get('The upload could not be confirmed. It may have completed. Please reload before trying again.', 'compressSpaces');
      this.changeDetectorRef.detectChanges();
    };

    xhr.open('POST', ticket.url, true);
    // NO setRequestHeader('Content-Type') — see the note above.
    xhr.send(buildUploadForm(ticket, file));
  }

}

